import React, {useEffect, useMemo, useRef, useState} from 'react';
import './App.css';
import AppRoutes from "./common/routes/AppRoutes";
import {BrowserRouter, useLocation, useNavigate} from "react-router-dom";
import Constants from "./common/Constants";
import keycloakService, {KeycloakService} from "./keycloak/KeycloakService";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import MomentUtils from "@date-io/moment";
import {Provider} from "react-redux";
import store from "./store/Store";
import {StyledEngineProvider, ThemeProvider} from "@mui/material";
import theme from "./common/Theme";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import "moment/locale/cs";
import tokenRefreshService, {TokenRefreshService} from "./common/TokenRefreshService";
import {KeycloakHolder} from "./keycloak/KeycloakHolder";
import LocaleContext, {Locale} from './common/LocaleContext';
import {Language, languageToLocale} from "./common/Language";
import userSettingsService from "./common/apis/user/UserSettingsService";
import EventManagerContext from './common/event/EventManagerContext';
import EventManager from "./common/event/EventManager";
import dayjs from "dayjs";
import en from "dayjs/locale/en"
import cs from "dayjs/locale/cs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import ConfigurationContext, {Configuration} from './common/ConfigurationContext';
import {FeatureStatusesDto} from "./configuration/FeatureStatusesDto";
import inaccessibleRouteService from "./common/routes/service/InaccessibleRouteService";
import {IPropertyDefinition} from "./common/apis/CommonTypes";
import elementService from "./pages/main/content/elementdetail/service/ElementService";
import ElementPropertyDefinitionContext, {ElementPropertyDefinition} from "./common/ElementPropertyDefinitionContext";
import diagramService from "./pages/main/content/diagrams/service/DiagramService";
import DiagramPropertyDefinitionContext from './common/DiagramPropertyDefinitionContext';

type Props = {
    keycloakService: KeycloakService,
    tokenRefreshService: TokenRefreshService,
}

function App({keycloakService, tokenRefreshService}: Readonly<Props>) {
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthRequired = location.pathname !== Constants.FE_APP_DIAGRAM_EXPORT_VIEW_ROUTE;
    const isAuthenticated = KeycloakHolder.keycloak && KeycloakHolder.getParsedToken() != null;

    useEffect(() => {
        function initTokenRefresh(onRefreshFailed: () => void) {
            if (KeycloakHolder.keycloak && KeycloakHolder.getParsedToken() != null) {
                tokenRefreshService.planTokenRefresh(KeycloakHolder.keycloak, KeycloakHolder.getParsedToken()!, onRefreshFailed);
            } else {
                throw new Error();
            }
        }

        if (!isAuthenticated && isAuthRequired) {
            (async () => {
                try {
                    const config = await keycloakService.fetchKeycloakConfig();
                    await keycloakService.doKeycloakLogin(config);
                    initTokenRefresh(() => {
                        navigate(Constants.FE_APP_LOGIN_FAILED_ROUTE);
                    });
                    inaccessibleRouteService.saveCurrent();
                    navigate(Constants.FE_APP_LOGIN_ROUTE);
                } catch (error) {
                    navigate(Constants.FE_APP_LOGIN_FAILED_ROUTE);
                }
            })();
        }
    }, [keycloakService, tokenRefreshService, isAuthenticated, isAuthRequired, navigate]);

    if (!isAuthenticated && isAuthRequired) {
        return <div/>;
    }
    return (
        <AppRoutes/>
    );
}

export default function AppWrapper() {
    const [language, setLanguage] = useState<Language>(Language.CS);
    const [featureStatuses, setFeatureStatuses] = useState<FeatureStatusesDto>({
        aiEnabled: false,
        graphQueryEnabled: false,
    });
    const [elementPropertyDefinitions, setElementPropertyDefinitions] = useState<IPropertyDefinition[]>([]);
    const [diagramsPropertyDefinitions, setDiagramsPropertyDefinitions] = useState<IPropertyDefinition[]>([]);

    const eventManager = useRef<EventManager>(new EventManager());

    const locale: Locale = useMemo(() => ({
        locale: languageToLocale(language) as string,
        language: language,
        setLanguage: updateLanguage
    }), [language]);

    useEffect(() => {
        dayjs.extend(localizedFormat);
        switch (language) {
            case Language.CS:
                dayjs.locale(cs);
                break;
            case Language.EN:
            default:
                dayjs.locale(en);
                break;
        }
    }, [language]);

    function updateLanguage(language: Language, store: boolean) {
        if (store) {
            userSettingsService.updateGeneralSettings({
                language: language
            });
        }
        setLanguage(language);
    }

    const configuration: Configuration = {
        featureStatuses: featureStatuses,
        setFeatureStatuses: setFeatureStatuses
    };

    const elementPropertyDefinition: ElementPropertyDefinition = {
        propertyDefinitions: elementPropertyDefinitions,
        updatePropertyDefinitions: updateElementPropertyDefinitions,
    }

    function updateElementPropertyDefinitions() {
        elementService.findAllPropertyDefinitions()
            .then(propertyDefinitions => {
                setElementPropertyDefinitions(propertyDefinitions);
            });
    }

    const diagramPropertyDefinition: ElementPropertyDefinition = {
        propertyDefinitions: diagramsPropertyDefinitions,
        updatePropertyDefinitions: updateDiagramPropertyDefinitions,
    }

    function updateDiagramPropertyDefinitions() {
        diagramService.findAllPropertyDefinitions()
            .then(propertyDefinitions => {
                setDiagramsPropertyDefinitions(propertyDefinitions);
            });
    }

    return (
        <ElementPropertyDefinitionContext.Provider value={elementPropertyDefinition}>
            <DiagramPropertyDefinitionContext.Provider value={diagramPropertyDefinition}>
                <ConfigurationContext.Provider value={configuration}>
                    <LocaleContext.Provider value={locale}>
                        <LocalizationProvider dateAdapter={AdapterMoment} utils={MomentUtils}
                                              adapterLocale={locale.locale}>
                            <Provider store={store as any}>
                                <StyledEngineProvider injectFirst>
                                    <ThemeProvider theme={theme}>
                                        <EventManagerContext.Provider value={eventManager.current}>
                                            <BrowserRouter>
                                                <App keycloakService={keycloakService}
                                                     tokenRefreshService={tokenRefreshService}/>
                                            </BrowserRouter>
                                        </EventManagerContext.Provider>
                                    </ThemeProvider>
                                </StyledEngineProvider>
                            </Provider>
                        </LocalizationProvider>
                    </LocaleContext.Provider>
                </ConfigurationContext.Provider>
            </DiagramPropertyDefinitionContext.Provider>
        </ElementPropertyDefinitionContext.Provider>
    );
}
